import {
  CONDITIONS,
  conditionName,
  DEAD,
  deadPatch,
  isDead,
  makesDeathSaves,
  MAP_TYPE_KEY,
  STATBLOCK_DE,
  naturalCompare,
  parseHpInput,
  statblockOf,
  type CombatActionResult,
  type CombatantDTO,
  type CombatDTO,
} from '@kartothek/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api, ApiError } from '../api';
import { Pips } from '../components/CharacterSheet';
import { Combobox, useNoteOptions } from '../components/Combobox';
import { MapView, mapImageUrl } from '../components/MapView';
import { NoteLink } from '../components/NoteLink';
import { StatblockView } from '../components/Statblock';
import { useNote, useNoteIndex } from '../hooks';
import { t, tDyn } from '../i18n';
import { classNames } from '../util';

const conditionLabel = conditionName;

// ---------- Übersicht ----------

export function CombatListPage() {
  const { cid = '' } = useParams();
  const navigate = useNavigate();
  const list = useQuery({ queryKey: ['combats', cid], queryFn: () => api.combats(cid) });
  const index = useNoteIndex(cid);
  const sessions = useMemo(
    () => (index.data ?? []).filter((n) => n.typeKey === 'session').sort((a, b) => naturalCompare(b.title, a.title)),
    [index.data],
  );
  const [name, setName] = useState('');
  const [sessionNoteId, setSessionNoteId] = useState('');

  useEffect(() => {
    document.title = `${t('combat.title')} · Kartothek`;
  }, []);
  useEffect(() => {
    if (!sessionNoteId && sessions[0]) setSessionNoteId(sessions[0].id);
  }, [sessions, sessionNoteId]);

  const create = async (e: React.FormEvent) => {
    e.preventDefault();
    const c = await api.createCombat(cid, { name: name.trim() || t('combat.defaultName'), sessionNoteId: sessionNoteId || null });
    navigate(`/c/${cid}/combat/${c.id}`);
  };

  return (
    <div className="page-narrow">
      <h1>{t('combat.title')}</h1>
      <form className="row-form" onSubmit={create}>
        <label>
          {t('common.name')}
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder={t('combat.defaultName')} />
        </label>
        <label>
          {t('combat.sessionNote')}
          <select value={sessionNoteId} onChange={(e) => setSessionNoteId(e.target.value)}>
            <option value="">{t('common.none')}</option>
            {sessions.map((s) => (
              <option key={s.id} value={s.id}>
                {s.title}
              </option>
            ))}
          </select>
        </label>
        <button className="primary" type="submit">
          + {t('combat.new')}
        </button>
      </form>
      {list.data?.length === 0 && <p className="muted">{t('combat.none')}</p>}
      <ul className="campaign-list">
        {list.data?.map((k) => (
          <li key={k.id}>
            <Link to={`/c/${cid}/combat/${k.id}`}>
              <strong>{k.name}</strong>
              <span className="muted small">
                {tDyn(`combat.status.${k.status}`)} · {t('combat.roundN', { n: k.round })} · {t('combat.participants', { n: k.combatantCount })}
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ---------- Tracker ----------

function HpBar({ c }: { c: CombatantDTO }) {
  if (c.hpMax === null || c.hpMax === 0) return null;
  const cur = c.hpCurrent ?? c.hpMax;
  const pct = Math.max(0, Math.min(100, (cur / c.hpMax) * 100));
  const tone = pct > 50 ? 'ok' : pct > 25 ? 'warn' : 'bad';
  return (
    <div className="hp-bar" aria-hidden>
      <div className={`hp-fill ${tone}`} style={{ width: `${pct}%` }} />
      {c.tempHp > 0 && <div className="hp-temp" style={{ width: `${Math.min(100, (c.tempHp / c.hpMax) * 100)}%` }} />}
    </div>
  );
}

function ConditionPicker(props: { onAdd: (key: string, rounds: number | null) => void; onClose: () => void }) {
  const [key, setKey] = useState('');
  const [rounds, setRounds] = useState('');
  return (
    <form
      className="condition-picker"
      onSubmit={(e) => {
        e.preventDefault();
        if (!key.trim()) return;
        const r = rounds.trim() === '' ? null : Math.max(1, Number(rounds));
        props.onAdd(key.trim(), Number.isFinite(r) ? r : null);
      }}
      onKeyDown={(e) => e.key === 'Escape' && props.onClose()}
    >
      <div className="condition-grid">
        {CONDITIONS.map((c) => (
          <button type="button" key={c} className={classNames('chip', key === c && 'on')} onClick={() => setKey(c)}>
            {conditionLabel(c)}
          </button>
        ))}
      </div>
      <div className="condition-row">
        <input autoFocus placeholder={t('combat.conditionCustom')} value={CONDITIONS.includes(key as never) ? '' : key} onChange={(e) => setKey(e.target.value)} />
        <input className="sb-num" type="number" min={1} placeholder={t('combat.rounds')} value={rounds} onChange={(e) => setRounds(e.target.value)} />
        <button className="primary" type="submit" disabled={!key.trim()}>
          {t('common.create')}
        </button>
        <button type="button" onClick={props.onClose}>
          {t('common.cancel')}
        </button>
      </div>
    </form>
  );
}

function CombatantRow(props: {
  cid: string;
  c: CombatantDTO;
  active: boolean;
  selected: boolean;
  onSelect: () => void;
  patch: (patch: Record<string, unknown>) => Promise<void>;
  hp: (input: string) => Promise<boolean>;
  remove: () => void;
  initRef: (el: HTMLInputElement | null) => void;
  onInitDone: () => void;
}) {
  const { c } = props;
  const [hpInput, setHpInput] = useState('');
  const [picking, setPicking] = useState(false);
  const [init, setInit] = useState(c.initiative === null ? '' : String(c.initiative));
  useEffect(() => setInit(c.initiative === null ? '' : String(c.initiative)), [c.initiative]);

  const commitInit = async () => {
    const raw = init.trim().toLowerCase();
    let value: number | null;
    if (raw === '') value = null;
    else if (/^[wd]\d+$/.test(raw)) value = Number(raw.slice(1)) + c.initBonus;
    else if (/^-?\d+$/.test(raw)) value = Number(raw);
    else return setInit(c.initiative === null ? '' : String(c.initiative));
    if (value !== c.initiative) await props.patch({ initiative: value, applyToGroup: !!c.groupId });
  };

  const cur = c.hpCurrent ?? c.hpMax;
  const dead = isDead(c);
  return (
    <li
      className={classNames('combatant', props.active && 'active', props.selected && 'selected', c.defeated && 'defeated', dead && 'dead', `kind-${c.kind}`)}
      onClick={props.onSelect}
    >
      <div className="cb-init" title={t('combat.initHint', { bonus: c.initBonus >= 0 ? `+${c.initBonus}` : String(c.initBonus) })}>
        <input
          ref={props.initRef}
          value={init}
          inputMode="numeric"
          onChange={(e) => setInit(e.target.value)}
          onBlur={() => void commitInit()}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              void commitInit().then(props.onInitDone);
            }
          }}
          aria-label={t('combat.initiative')}
        />
        <span className="muted small">{c.initBonus >= 0 ? `+${c.initBonus}` : c.initBonus}</span>
      </div>
      <div className="cb-name">
        {props.active && <span className="turn-marker">▶</span>}
        {c.noteId ? (
          <NoteLink cid={props.cid} noteId={c.noteId} preview={false}>
            {c.name}
          </NoteLink>
        ) : (
          <span>{c.name}</span>
        )}
        <span className={`kind-badge kind-${c.kind}`}>{tDyn(`combat.kind.${c.kind}`)}</span>
        {c.concentration && <span className="conc-badge" title={t('combat.concentration')}>◎</span>}
        {dead && <span className="dead-badge">✝ {t('combat.dead')}</span>}
      </div>
      <div className="cb-ac" title={STATBLOCK_DE.labels.ac}>
        <span className="muted small">{STATBLOCK_DE.labels.acShort}</span> {c.ac ?? '–'}
      </div>
      <div className="cb-hp">
        <div className="cb-hp-text">
          <strong>{cur ?? '–'}</strong>
          {c.hpMax !== null && <span className="muted">/{c.hpMax}</span>}
          {c.tempHp > 0 && <span className="temp-badge">+{c.tempHp}</span>}
        </div>
        <HpBar c={c} />
        <input
          className="hp-input"
          disabled={dead}
          value={hpInput}
          placeholder="-7 / +5 / t8"
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => setHpInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              void props.hp(hpInput).then((ok) => ok && setHpInput(''));
            }
          }}
          aria-label={t('combat.hpInput')}
        />
      </div>
      <div className="cb-conditions">
        {makesDeathSaves(c) && cur === 0 && !dead && (
          <span className="cb-death" title={t('sheet.deathSaves')} onClick={(e) => e.stopPropagation()}>
            ✓
            <Pips total={3} filled={c.deathSaves.success} tone="ok" label={t('sheet.deathSuccess')} onChange={(n) => void props.patch({ deathSaves: { ...c.deathSaves, success: n } })} />
            ✗
            <Pips total={3} filled={c.deathSaves.failure} tone="bad" label={t('sheet.deathFailure')} onChange={(n) => void props.patch({ deathSaves: { ...c.deathSaves, failure: n } })} />
          </span>
        )}
        {c.conditions.map((x, i) => x.key !== DEAD && (
          <button
            key={`${x.key}-${i}`}
            className="cond-chip"
            disabled={dead}
            title={t('combat.removeCondition')}
            onClick={(e) => {
              e.stopPropagation();
              void props.patch({ conditions: c.conditions.filter((_, j) => j !== i) });
            }}
          >
            {conditionLabel(x.key)}
            {x.rounds !== null && <span className="rounds">{x.rounds}</span>}
          </button>
        ))}
        <button
          className="cond-add"
          disabled={dead}
          title={t('combat.addCondition')}
          onClick={(e) => {
            e.stopPropagation();
            setPicking((p) => !p);
          }}
        >
          +
        </button>
        {picking && (
          <div onClick={(e) => e.stopPropagation()}>
            <ConditionPicker
              onClose={() => setPicking(false)}
              onAdd={(key, rounds) => {
                setPicking(false);
                void props.patch({ conditions: [...c.conditions, { key, rounds }] });
              }}
            />
          </div>
        )}
      </div>
      <div className="cb-actions" onClick={(e) => e.stopPropagation()}>
        <button
          className={classNames('icon-btn', c.concentration && 'on')}
          disabled={dead}
          title={t('combat.concentration')}
          onClick={() => void props.patch({ concentration: !c.concentration })}
        >
          ◎
        </button>
        <button className={classNames('icon-btn', c.defeated && 'on')} disabled={dead} title={t('combat.defeated')} onClick={() => void props.patch({ defeated: !c.defeated })}>
          ☠
        </button>
        {(c.kind === 'pc' || c.kind === 'npc') && (
          <button
            className={classNames('icon-btn', dead && 'on')}
            title={dead ? t('combat.undoDead') : t('combat.dead')}
            onClick={() => {
              if (!dead && !confirm(t('combat.deadConfirm', { name: c.name }))) return;
              void props.patch(dead ? { ...deadPatch(c, false), deathSaves: { success: 0, failure: 0 } } : deadPatch(c, true));
            }}
          >
            ✝
          </button>
        )}
        <button className="icon-btn danger" title={t('combat.remove')} onClick={props.remove}>
          ×
        </button>
      </div>
    </li>
  );
}

function SidePanel({ cid, c }: { cid: string; c: CombatantDTO | undefined }) {
  const note = useNote(cid, c?.noteId ?? undefined);
  if (!c) return null;
  const sb = note.data ? statblockOf(note.data.frontmatter) : null;
  return (
    <div className="combat-side">
      {sb && note.data ? (
        <StatblockView statblock={sb} fallbackName={note.data.title} cid={cid} links={note.data.links} folder={note.data.folder} isGameMaster compact />
      ) : (
        <p className="muted small">{c.noteId ? t('common.loading') : t('combat.noStatblock')}</p>
      )}
    </div>
  );
}

/** Kampfkarte unter dem Hinzufügen-Feld; Klick auf einen Token wählt den Teilnehmer. */
function CombatMap(props: {
  cid: string;
  mapNoteId: string;
  selectedId: string | null;
  onSelect: (combatantId: string) => void;
  onViewCenter: (center: { x: number; y: number }) => void;
}) {
  const note = useNote(props.cid, props.mapNoteId);
  if (!note.data) return <div className="muted small">{t('common.loading')}</div>;
  return (
    <MapView
      note={note.data}
      gm
      className="combat-map"
      highlightCombatantId={props.selectedId}
      onTokenSelect={(tk) => tk.combatantId && props.onSelect(tk.combatantId)}
      onViewCenter={props.onViewCenter}
    />
  );
}

/** Bildmitte einer Karte – damit neue Tokens nicht im Bildrand landen (Größe notfalls im Browser ermitteln). */
async function mapCenter(cid: string, mapNoteId: string): Promise<{ x: number; y: number } | undefined> {
  const m = await api.map(cid, mapNoteId);
  if (m.image) return { x: m.image.width / 2, y: m.image.height / 2 };
  const url = mapImageUrl(await api.note(cid, mapNoteId));
  if (!url) return undefined;
  const img = await new Promise<HTMLImageElement | null>((resolve) => {
    const el = new Image();
    el.onload = () => resolve(el);
    el.onerror = () => resolve(null);
    el.src = url;
  });
  if (!img) return undefined;
  await api.updateMap(cid, mapNoteId, { image: { width: img.width, height: img.height } });
  return { x: img.width / 2, y: img.height / 2 };
}

function MapPicker(props: { cid: string; value: string | null; onChange: (id: string | null) => void }) {
  const index = useNoteIndex(props.cid);
  const maps = useMemo(
    () => (index.data ?? []).filter((n) => n.typeKey === MAP_TYPE_KEY).sort((a, b) => naturalCompare(a.title, b.title)),
    [index.data],
  );
  return (
    <label className="inline small combat-map-picker">
      {t('combat.map')}
      <select value={props.value ?? ''} onChange={(e) => props.onChange(e.target.value || null)}>
        <option value="">{t('combat.mapNone')}</option>
        {maps.map((m) => (
          <option key={m.id} value={m.id}>
            {m.title}
          </option>
        ))}
      </select>
    </label>
  );
}

function AddPanel({
  cid,
  kid,
  onCombat,
  spawn,
}: {
  cid: string;
  kid: string;
  onCombat: (c: CombatDTO) => void;
  /** Mitte der sichtbaren Kampfkarte – dort erscheinen die neuen Tokens. */
  spawn: () => { x: number; y: number } | undefined;
}) {
  const options = useNoteOptions(cid, ['monster', 'nsc', 'charakter']);
  const candidates = useMemo(() => options.map((o) => o.note), [options]);
  const [text, setText] = useState('');
  const [count, setCount] = useState(1);
  const [hp, setHp] = useState('');
  const [ac, setAc] = useState('');
  const [error, setError] = useState<string | null>(null);
  const countRef = useRef<HTMLInputElement>(null);

  const add = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!text.trim()) return;
    setError(null);
    try {
      const hit = candidates.find((n) => n.title.toLowerCase() === text.trim().toLowerCase());
      const res = hit
        ? await api.addCombatants(cid, kid, { noteId: hit.id, count, spawn: spawn() })
        : await api.addCombatants(cid, kid, {
            name: text.trim(),
            count,
            spawn: spawn(),
            hpMax: hp ? Number(hp) : undefined,
            ac: ac ? Number(ac) : undefined,
          });
      onCombat(res);
      setText('');
      setCount(1);
      setHp('');
      setAc('');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    }
  };
  const isNote = candidates.some((n) => n.title.toLowerCase() === text.trim().toLowerCase());

  return (
    <form className="combat-add" onSubmit={add}>
      <Combobox
        value={text}
        onChange={setText}
        options={options}
        limit={30}
        onPick={(o) => {
          setText(o.title);
          countRef.current?.select();
        }}
        placeholder={t('combat.addPlaceholder')}
      />
      <label className="inline small">
        ×
        <input
          ref={countRef}
          className="sb-num"
          type="number"
          min={1}
          max={30}
          value={count}
          onChange={(e) => setCount(Math.max(1, Number(e.target.value) || 1))}
        />
      </label>
      {!isNote && text.trim() && (
        <>
          <input className="sb-num" type="number" min={0} placeholder={STATBLOCK_DE.labels.hpShort} value={hp} onChange={(e) => setHp(e.target.value)} />
          <input className="sb-num" type="number" min={0} placeholder={STATBLOCK_DE.labels.acShort} value={ac} onChange={(e) => setAc(e.target.value)} />
        </>
      )}
      <button className="primary" type="submit" disabled={!text.trim()}>
        + {t('combat.add')}
      </button>
      <button type="button" onClick={() => void api.addAllCharacters(cid, kid, spawn()).then(onCombat)}>
        + {t('combat.addCharacters')}
      </button>
      {error && <span className="error small">{error}</span>}
    </form>
  );
}

export function CombatPage() {
  const { cid = '', kid = '' } = useParams();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const q = useQuery({ queryKey: ['combat', cid, kid], queryFn: () => api.combat(cid, kid) });
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showMap, setShowMap] = useState(() => {
    try {
      return localStorage.getItem('kt:combat-map') !== '0';
    } catch {
      return true;
    }
  });
  const [notices, setNotices] = useState<{ id: number; text: string; tone?: 'warn' }[]>([]);
  const [error, setError] = useState<string | null>(null);
  const initRefs = useRef(new Map<string, HTMLInputElement>());
  const viewCenter = useRef<{ x: number; y: number } | undefined>(undefined);
  const onViewCenter = useCallback((p: { x: number; y: number }) => {
    viewCenter.current = p;
  }, []);
  const noticeId = useRef(0);
  const combat = q.data;

  useEffect(() => {
    if (combat) document.title = `${combat.name} · ${t('combat.title')} · Kartothek`;
  }, [combat]);

  const setCombat = useCallback(
    (c: CombatDTO) => {
      qc.setQueryData(['combat', cid, kid], c);
      void qc.invalidateQueries({ queryKey: ['combats', cid] });
      // Tokens zeigen TP, Zustände und den aktiven Zug
      if (c.mapNoteId) void qc.invalidateQueries({ queryKey: ['map', cid, c.mapNoteId] });
      // Tod, Todesrettungswürfe und TP werden in die Notizen übertragen
      void qc.invalidateQueries({ queryKey: ['note', cid] });
    },
    [qc, cid, kid],
  );

  const notify = useCallback((texts: string[], tone?: 'warn') => {
    if (texts.length === 0) return;
    const items = texts.map((text) => ({ id: ++noticeId.current, text, tone }));
    setNotices((n) => [...n, ...items].slice(-6));
    window.setTimeout(() => setNotices((n) => n.filter((x) => !items.some((i) => i.id === x.id))), 8000);
  }, []);

  const run = useCallback(
    async <T,>(fn: () => Promise<T>): Promise<T | undefined> => {
      try {
        setError(null);
        return await fn();
      } catch (e) {
        setError(e instanceof ApiError ? e.message : String(e));
        return undefined;
      }
    },
    [],
  );

  const handleAction = useCallback(
    (res: CombatActionResult | undefined) => {
      if (!res) return;
      setCombat(res.combat);
      notify(res.notices.filter((n) => n.startsWith('Konzentrationswurf') || n.endsWith('endet') || n.includes('bewusstlos') || n.includes('stirbt') || n.includes('Todesrettungswurf')), 'warn');
    },
    [setCombat, notify],
  );

  const turn = useCallback(
    async (direction: 1 | -1) => handleAction(await run(() => api.turn(cid, kid, direction))),
    [cid, kid, run, handleAction],
  );

  // Tastatur: N = nächster Zug, Umschalt+N = zurück (nicht in Eingabefeldern)
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement;
      if (el.closest('input, textarea, select, [contenteditable]')) return;
      if (e.key.toLowerCase() === 'n' && !e.ctrlKey && !e.metaKey && combat?.status !== 'ended') {
        e.preventDefault();
        void turn(e.shiftKey ? -1 : 1);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [turn, combat?.status]);

  if (q.isLoading) return <div className="muted pad">{t('common.loading')}</div>;
  if (!combat) return <div className="pad error">{t('combat.notFound')}</div>;

  const order = combat.combatants;
  const active = order.find((c) => c.id === combat.activeCombatantId);
  const selected = order.find((c) => c.id === selectedId) ?? active ?? order[0];
  const ended = combat.status === 'ended';

  const focusNextInit = (id: string) => {
    const i = order.findIndex((c) => c.id === id);
    const next = order.slice(i + 1).find((c) => c.initiative === null) ?? order[i + 1];
    if (next) initRefs.current.get(next.id)?.focus();
  };

  return (
    <div className="combat-page">
      <header className="combat-head">
        <div>
          <Link to={`/c/${cid}/combat`} className="muted small">
            ← {t('combat.title')}
          </Link>
          <h1>{combat.name}</h1>
          <div className="muted small combat-sub">
            {tDyn(`combat.status.${combat.status}`)}
            {combat.sessionNoteId && (
              <>
                {' · '}
                <NoteLink cid={cid} noteId={combat.sessionNoteId} preview={false}>
                  {t('combat.sessionNote')}
                </NoteLink>
              </>
            )}
          </div>
        </div>
        <div className="round-box">
          <span className="muted small">{t('combat.round')}</span>
          <strong>{combat.round || '–'}</strong>
        </div>
        <div className="combat-controls">
          <button disabled={ended || combat.status === 'prep'} onClick={() => void turn(-1)} title="Umschalt+N">
            ◀ {t('combat.prev')}
          </button>
          <button className="primary big" disabled={ended || order.length === 0} onClick={() => void turn(1)} title="N">
            {combat.status === 'prep' ? t('combat.start') : t('combat.next')} ▶
          </button>
          {!ended ? (
            <button
              onClick={async () => {
                if (!confirm(t('combat.endConfirm'))) return;
                const res = await run(() => api.endCombat(cid, kid, true));
                if (res) setCombat(res);
              }}
            >
              {t('combat.end')}
            </button>
          ) : (
            <button
              className="danger"
              onClick={async () => {
                if (!confirm(t('combat.deleteConfirm'))) return;
                await api.deleteCombat(cid, kid);
                void qc.invalidateQueries({ queryKey: ['combats', cid] });
                navigate(`/c/${cid}/combat`);
              }}
            >
              {t('common.delete')}
            </button>
          )}
        </div>
      </header>
      {error && <div className="error banner">{error}</div>}
      <div className="notices">
        {notices.map((n) => (
          <div key={n.id} className={classNames('notice', n.tone)}>
            {n.text}
          </div>
        ))}
      </div>
      {!ended && <AddPanel cid={cid} kid={kid} onCombat={setCombat} spawn={() => (combat.mapNoteId && showMap ? viewCenter.current : undefined)} />}
      <div className="combat-map-bar">
        <MapPicker
          cid={cid}
          value={combat.mapNoteId}
          onChange={async (mapNoteId) => {
            const spawn = mapNoteId ? await mapCenter(cid, mapNoteId).catch(() => undefined) : undefined;
            const res = await run(() => api.updateCombat(cid, kid, { mapNoteId, spawn }));
            if (res) setCombat(res);
          }}
        />
        {combat.mapNoteId && (
          <button
            className={classNames('small', showMap && 'on')}
            onClick={() =>
              setShowMap((v) => {
                try {
                  localStorage.setItem('kt:combat-map', v ? '0' : '1');
                } catch {
                  /* egal */
                }
                return !v;
              })
            }
          >
            🗺 {t('combat.showMap')}
          </button>
        )}
        {combat.mapNoteId && (
          <Link className="button small" to={`/c/${cid}/map/${combat.mapNoteId}`}>
            ⛶ {t('map.fullscreen')}
          </Link>
        )}
      </div>
      {combat.mapNoteId && showMap && <CombatMap cid={cid} mapNoteId={combat.mapNoteId} selectedId={selected?.id ?? null} onSelect={setSelectedId} onViewCenter={onViewCenter} />}
      <div className="combat-grid">
        <div>
          {order.length === 0 ? (
            <p className="muted">{t('combat.empty')}</p>
          ) : (
            <ul className="combatants">
              {order.map((c) => (
                <CombatantRow
                  key={c.id}
                  cid={cid}
                  c={c}
                  active={c.id === combat.activeCombatantId}
                  selected={c.id === selected?.id}
                  onSelect={() => setSelectedId(c.id)}
                  initRef={(el) => {
                    if (el) initRefs.current.set(c.id, el);
                    else initRefs.current.delete(c.id);
                  }}
                  onInitDone={() => focusNextInit(c.id)}
                  patch={async (patch) => {
                    const res = await run(() => api.updateCombatant(cid, kid, c.id, patch));
                    if (res) setCombat(res);
                  }}
                  hp={async (input) => {
                    const parsed = parseHpInput(input);
                    if (!parsed) {
                      setError(t('combat.hpInvalid'));
                      return false;
                    }
                    handleAction(await run(() => api.changeHp(cid, kid, c.id, parsed.kind, parsed.amount)));
                    return true;
                  }}
                  remove={async () => {
                    if (!confirm(t('combat.removeConfirm', { name: c.name }))) return;
                    const res = await run(() => api.removeCombatant(cid, kid, c.id));
                    if (res) setCombat(res);
                  }}
                />
              ))}
            </ul>
          )}
          <p className="muted small">{t('combat.help')}</p>
          <details className="combat-log">
            <summary>
              {t('combat.log')} <span className="count">{combat.log.length}</span>
            </summary>
            <ol reversed>
              {[...combat.log].reverse().map((l, i) => (
                <li key={i}>
                  <span className="muted small">R{l.round}</span> {l.text}
                </li>
              ))}
            </ol>
          </details>
        </div>
        <SidePanel cid={cid} c={selected} />
      </div>
    </div>
  );
}
